<template>
  <div class="auto-complete-input-container">
    <mobialis-text-input-with-validation
      :label="label"
      :rules="{
        included: this.sourceData ? this.sourceData.map(item => (typeof item === 'object' ? item.value : item)) : [],
        required: true,
      }"
      :hiddenLabel="hiddenLabel"
      :disabled="disabled"
      v-model="autoCompleteInputValInner"
      :id="id"
      :vid="vid"
      tabindex="1"
    >
    </mobialis-text-input-with-validation>
  </div>
</template>

<script>
import autoComplete from '@tarekraafat/autocomplete.js';
import helperFunctions from '@/utils/helperFunctions';
import { AVAnalyticsEventTriggers } from '@/utils/enums';

export default {
  name: 'AutoComplete',
  props: {
    sourceData: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      required: true,
    },
    hiddenLabel: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    vid: {
      type: String,
      default: 'autoComplete',
    },
    id: {
      type: String,
      default: 'autoComplete',
    },
    shouldAlwaysDisplayAllResults: {
      type: Boolean,
      default: true,
    },
    shouldKeepActiveSelection: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      autoCompleteInputValInner: '',
      lastValidSelection: '',
    };
  },
  watch: {
    autoCompleteInputValInner(newVal) {
      this.$emit('input', newVal);
    },
    value(newVal) {
      this.autoCompleteInputValInner = newVal;
    },
  },
  created() {
    if (this.value) {
      this.autoCompleteInputValInner = this.value;
    }
  },
  mounted() {
    this.lastValidSelection = this.value || this.autoCompleteInputValInner;
    const thisRoutePath = this.$route.path;
    const thisStore = this.$store;
    let firstInteractionSent = false;
    // eslint-disable-next-line no-shadow,new-cap
    const autoCompleteJS = new autoComplete({
      selector: `#${this.id}`,
      placeHolder: this.placeholder,
      threshold: 0,
      resultsList: {
        maxResults: undefined,
        noResults: true,
        element: (list, data) => {
          if (!data.results.length) {
            const message = document.createElement('div');
            message.setAttribute('class', 'no-results');
            message.innerHTML = `<span>${this.$t('message.common.noResultsFound', { query: data.query })}</span>`;
            list.appendChild(message);
          }
        },
      },
      data: {
        src: this.sourceData,
        ...(Array.isArray(this.sourceData) && typeof this.sourceData[0] === 'object' ? { keys: ['value'] } : {}),
        cache: true,
      },
      resultItem: {
        highlight: true,
      },
      events: {
        input: {
          selection: event => {
            const inputValue =
              typeof event.detail.selection.value === 'object' && event.detail.selection.value !== null
                ? event.detail.selection.value.value
                : event.detail.selection.value;
            // Set a change event manually, on first change it is not automatically triggered,
            // the change event will trigger our av analytics event for form engagement (src/views/shared/App.vue - inputChangeEventListener)
            if (this.autoCompleteInputValInner !== inputValue && firstInteractionSent === false) {
              autoCompleteJS.input.dispatchEvent(new Event('change', { bubbles: true }));
              firstInteractionSent = true;
            }
            this.autoCompleteInputValInner = inputValue;
            this.$emit('selectedValue', event);
            this.$emit('onSelect', inputValue);
            this.lastValidSelection = inputValue;
            autoCompleteJS.input.blur();
          },
          focus: event => {
            // On focus, always show the full dropdown list by temporarily clearing the input value and restoring it.
            // This ensures users see all options right away, not just filtered ones – key UX req for easy re-selection.
            if (!this.shouldAlwaysDisplayAllResults) {
              autoCompleteJS.start();
              return;
            }

            helperFunctions.sendAVAnalyticsEvent(thisRoutePath, AVAnalyticsEventTriggers.INTERACTION, thisStore);
            // On every focus, temporarily clear the input, invoke the search, then restore the input value.
            // This makes the autocomplete act like the input is empty, so it displays all results, but doesn't erase user selection.
            const inputElem = event.target;
            const currentValue = inputElem.value;

            inputElem.value = '';
            autoCompleteJS.start();
            setTimeout(() => {
              inputElem.value = currentValue;
            }, 0);
          },
          blur: event => {
            // On blur, accept the typed value if it exactly matches a valid option.
            // Otherwise, revert to the last valid selection (even if it's no longer in the data set).
            // This guarantees the input always contains either the latest valid value or a just-typed, valid one,
            // supporting UX where legacy/removed values remain selectable until user changes them.
            const inputVal = this.autoCompleteInputValInner;
            const validValues = this.sourceData.map(item => (typeof item === 'object' ? item.value : item));

            if (!this.shouldKeepActiveSelection) {
              this.$emit('onSelect', inputVal);
              setTimeout(() => {
                autoCompleteJS.close();
              }, 0);
              return;
            }

            if (inputVal !== this.lastValidSelection && inputVal !== '') {
              if (validValues.includes(inputVal)) {
                this.lastValidSelection = inputVal;
                this.$emit('onSelect', inputVal);
              } else {
                this.autoCompleteInputValInner = this.lastValidSelection;
                const inputEl = event.target;
                inputEl.value = (!this.lastValidSelection ? this.value : this.lastValidSelection) || '';
                this.$emit('onSelect', { value: this.lastValidSelection, isChanged: false });
              }
            }

            setTimeout(() => {
              autoCompleteJS.close();
            }, 0);
          },
        },
      },
    });
  },
};
</script>
